import {
  useFileRecord,
  useFileRecordsByIds,
  usePublishFileRecord,
  usePublishFileRecords,
  useUnpublishFileRecord,
  useUnpublishFileRecords,
} from "@/api/fileRecords";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCanvasOnboardingState } from "@/pages/canvas/context/CanvasOnboardingContext";
import { PUBLISH_CONFIRM_GLOW_STYLES } from "@/pages/canvas/nodes/widgets/onboarding-welcome/styles";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { FileRecordTypeIcon } from "@cohesive/ui-components";
import {
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  FileText,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useHtmlReferencedIds } from "./content/htmlReferencedIds";
import { classifyFileRecord, getFileRecordName } from "./contentType";
import { useHtmlInlineEditState } from "./HtmlInlineEditContext";
import {
  derivePublishVisualState,
  fileRecordDisplayName,
  fileRecordEverPublished,
  fileRecordIsPublishedAndCurrent,
  getPublishButtonStyling,
  getPublishStatusDotClass,
} from "./publish";

const SECTION_DIVIDER = "my-1 h-px bg-border/40 dark:bg-n-95/[0.06]";

/**
 * Header trigger + popover for publishing a file record and (for HTML)
 * any linked Cohesive files it embeds. Lives alongside the rest of the
 * file-record detail header actions and consolidates the per-file
 * public-URL controls (copy / open external) so the header stays
 * uncluttered for every kind.
 *
 * Visual language mirrors `AccountPopover`: `rounded-2xl` panel, hairline
 * `bg-border/40` section dividers, compact `px-3 py-2.5` rows, and the
 * same `text-[14px]/[12px]/[11px]` hierarchy.
 *
 * Dependency discovery is HTML-only and shared with `HtmlContent` via the
 * `useHtmlReferencedIds` query so we don't double-fetch the body. For
 * non-HTML kinds the dep query is short-circuited via `enabled: false`
 * and the popover renders the simpler form (status + link + open + footer).
 */
export function FilePublishButton({
  record,
  className,
  embeddedPanel = false,
  onEmbeddedClose,
  onEmbeddedSuccess,
}: {
  record: FileRecord;
  className?: string;
  /** Render only the publish panel (no trigger popover) — used by action-dialog shell. */
  embeddedPanel?: boolean;
  onEmbeddedClose?: () => void;
  /** Fired when a publish completes successfully, so the action-dialog callable can resolve `true`. */
  onEmbeddedSuccess?: () => void;
}) {
  const { highlightTargetId } = useCanvasOnboardingState();
  // When the outer publish button is the onboarding spotlight target,
  // also gently glow the inner "Publish" action inside the popover so
  // users who don't see the outer halo (or who open the popover via
  // some other gesture) still get a clear cue once it's revealed.
  const onboardingPublishActive =
    highlightTargetId === "artifact-publish-button";
  const publishFileRecord = usePublishFileRecord();
  const publishFileRecords = usePublishFileRecords();
  const unpublishFileRecord = useUnpublishFileRecord();
  const unpublishFileRecords = useUnpublishFileRecords();

  const [open, setOpen] = useState(false);
  const [linkedFilesOpen, setLinkedFilesOpen] = useState(false);
  const [publishAllInFlight, setPublishAllInFlight] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [cascadeUnpublishDeps, setCascadeUnpublishDeps] = useState(false);
  const [unpublishInFlight, setUnpublishInFlight] = useState(false);

  const kind = classifyFileRecord(record);
  const { pendingCount: htmlPendingCount, isSaving: htmlIsSaving } =
    useHtmlInlineEditState();
  const htmlEditsBlocking =
    kind === "html" && (htmlPendingCount > 0 || htmlIsSaving);

  const { data: htmlMeta, isLoading: htmlMetaLoading } = useHtmlReferencedIds(
    record,
    { enabled: kind === "html" },
  );
  const referencedFiles = htmlMeta?.referencedFiles ?? [];
  const referencedIds = referencedFiles.map((f) => f.id);

  // `retry: false` — a referenced file can legitimately belong to
  // another account (onboarding decks embed shared template assets the
  // viewing user doesn't own), so a 404 should settle on the first
  // attempt instead of retrying and leaving the publish action stuck
  // disabled. See note in handlePublishAll about why we don't `useMemo`
  // the query list.
  const referencedQueries = useFileRecordsByIds(referencedIds, {
    retry: false,
  });

  // Pair each referenced file with its fetch result. `referencedFiles`
  // is deduped by id, so it lines up index-for-index with the queries
  // `useFileRecordsByIds` returns. A file whose record couldn't be
  // loaded (`record: null`) is still surfaced in the popover — linked
  // via the public URL scraped from the HTML — rather than dropped.
  const linkedFiles = referencedFiles.map((file, i) => ({
    ...file,
    record: referencedQueries[i]?.data ?? null,
  }));
  const liveReferencedRecords: FileRecord[] = linkedFiles
    .map((f) => f.record)
    .filter((r): r is FileRecord => r !== null);
  // "Settled" = neither loading nor still pending (every query has a
  // result *or* an error). We deliberately don't gate on every id having
  // produced a record, because a referenced file can be deleted /
  // inaccessible / 404 and its query will settle into an error state with
  // no `data` — requiring a record per id would leave the publish action
  // disabled forever in that case. Failed deps just get skipped from
  // `liveReferencedRecords` and the user can still publish the doc.
  const referencedQueriesSettled = referencedQueries.every(
    (q) => !q.isLoading && !q.isPending,
  );

  // Subscribe (don't snapshot) to the per-id detail cache so publish /
  // unpublish mutations — which write the fresh record into this exact
  // key on success — push directly into this component. A one-shot
  // `queryClient.getQueryData` read here would leave the popover showing
  // a stale `published_url` until some other state change re-rendered
  // it, producing the flash-of-old-link bug after Unpublish.
  const { data: subscribedRecord } = useFileRecord(record.id, {
    enabled: !!record.id,
  });
  const liveDocumentRecord = subscribedRecord ?? record;

  const unpublishedDeps = liveReferencedRecords.filter(
    (r) => !fileRecordEverPublished(r),
  );
  const publishedDeps = liveReferencedRecords.filter((r) =>
    fileRecordEverPublished(r),
  );
  const documentEverPublished = fileRecordEverPublished(liveDocumentRecord);
  const documentCurrent = fileRecordIsPublishedAndCurrent(liveDocumentRecord);
  const hasUnpublishedDeps = unpublishedDeps.length > 0;
  const isFullyUpToDate = documentCurrent && !hasUnpublishedDeps;

  const isPublishing =
    publishAllInFlight ||
    publishFileRecord.isPending ||
    publishFileRecords.isPending;
  const isUnpublishing =
    unpublishInFlight ||
    unpublishFileRecord.isPending ||
    unpublishFileRecords.isPending;
  const isMutating = isPublishing || isUnpublishing;

  const visualState = derivePublishVisualState({
    documentEverPublished,
    isFullyUpToDate,
  });
  const styling = getPublishButtonStyling(visualState);
  const statusDotClass = getPublishStatusDotClass(visualState);

  // `published_url` is left on the row even after Unpublish (it's the
  // historical artifact, not the live state). Gate the link surface on
  // the `published` flag so the popover reflects the real publish state
  // immediately — otherwise the dead URL hangs around until something
  // else nulls the field on the cached row (often "never").
  //
  // The backend stores the raw CloudFront URL (`{host}/public/{id}`),
  // but the user-facing route lives under `/view/public/{id}` (the
  // `/view` prefix is the public viewer page; `/public/{id}` alone
  // serves the raw asset). Inject `/view` here so every surface in
  // this popover (copy field, "open in new tab", input value) hands
  // the user the viewer URL rather than the raw asset URL.
  const rawPublishedUrl = documentEverPublished
    ? liveDocumentRecord.published_url
    : undefined;
  const publishedUrl = rawPublishedUrl
    ? rawPublishedUrl.replace("/public/", "/view/public/")
    : undefined;
  const documentName = getFileRecordName(record) || "Untitled document";

  const handlePublishAll = useCallback(async () => {
    setPublishAllInFlight(true);
    try {
      // Publish dependencies first so the public URLs resolve by the time
      // the document itself goes live. `publishFileRecords` fans out under
      // the hood but returns per-id outcomes so we can surface partial
      // failures precisely.
      const depIds = unpublishedDeps.map((r) => r.id);
      if (depIds.length > 0) {
        const depResults = await publishFileRecords.mutateAsync(depIds);
        const failedDeps = depResults.filter((r) => r.status === "rejected");
        if (failedDeps.length > 0) {
          toast.error(
            failedDeps.length === 1
              ? "1 linked file failed to publish"
              : `${failedDeps.length} linked files failed to publish`,
          );
          return;
        }
      }

      await publishFileRecord.mutateAsync(record.id);
      toast.success("Published");
      onEmbeddedSuccess?.();
    } catch {
      toast.error("Failed to publish");
    } finally {
      setPublishAllInFlight(false);
    }
  }, [
    onEmbeddedSuccess,
    publishFileRecord,
    publishFileRecords,
    record.id,
    unpublishedDeps,
  ]);

  const handleUnpublish = useCallback(async () => {
    setUnpublishInFlight(true);
    try {
      // Unpublish the document first so its public URL stops resolving
      // immediately. Then optionally cascade to linked files. Doing it in
      // this order means a partial failure on the deps still leaves the
      // doc itself private (the safer end state).
      await unpublishFileRecord.mutateAsync(record.id);

      if (cascadeUnpublishDeps && publishedDeps.length > 0) {
        const depResults = await unpublishFileRecords.mutateAsync(
          publishedDeps.map((r) => r.id),
        );
        const failedDeps = depResults.filter((r) => r.status === "rejected");
        if (failedDeps.length > 0) {
          toast.error(
            failedDeps.length === 1
              ? "Document unpublished, but 1 linked file failed to unpublish"
              : `Document unpublished, but ${failedDeps.length} linked files failed to unpublish`,
          );
          setUnpublishConfirmOpen(false);
          return;
        }
      }

      toast.success("Unpublished");
      setUnpublishConfirmOpen(false);
    } catch {
      toast.error("Failed to unpublish");
    } finally {
      setUnpublishInFlight(false);
    }
  }, [
    cascadeUnpublishDeps,
    publishedDeps,
    record.id,
    unpublishFileRecord,
    unpublishFileRecords,
  ]);

  // Radix's outside-click detection runs on `pointerdown` in the parent
  // document, but pointer events that originate inside an `<iframe>` (the
  // HTML preview renderer) never surface there — the iframe owns its own
  // event loop. The OS-level signal we *can* observe is the parent window
  // losing focus to the iframe: when the user clicks inside it,
  // `window.blur` fires and `document.activeElement` becomes the iframe
  // element. We use that to close the popover the way an outside click
  // normally would.
  useEffect(() => {
    if (!open) return;
    const handleWindowBlur = () => {
      if (document.activeElement?.tagName === "IFRAME") {
        setOpen(false);
      }
    };
    window.addEventListener("blur", handleWindowBlur);
    return () => window.removeEventListener("blur", handleWindowBlur);
  }, [open]);

  // Close the popover when onboarding reaches the "your deck is live"
  // step. That step spotlights this button to show the published link;
  // an open popover left over from the user's publish click would cover
  // the spotlight. The signal arrives as a window event because the
  // file-record detail pane renders outside `CanvasOnboardingProvider`
  // (it lives in the layout-level right pane), so React context can't
  // reach it — the rest of the onboarding flow uses window events for
  // the same reason.
  useEffect(() => {
    const handleClose = () => setOpen(false);
    window.addEventListener(
      "cohesive:onboarding:close-publish-popover",
      handleClose,
    );
    return () =>
      window.removeEventListener(
        "cohesive:onboarding:close-publish-popover",
        handleClose,
      );
  }, []);

  const handleCopyUrl = useCallback(async () => {
    if (!publishedUrl) return;
    try {
      await navigator.clipboard.writeText(publishedUrl);
      toast.success("URL copied to clipboard");
    } catch {
      toast.error("Failed to copy URL");
    }
  }, [publishedUrl]);

  // Block the publish action until the linked-file picture is fully
  // known: the HTML body fetch that discovers referenced ids must have
  // settled, AND every referenced FileRecord query must have resolved or
  // errored. Without the body-fetch gate, `referencedIds` is an empty
  // list while the body is still loading — which reads as "settled" and
  // briefly ENABLES the button before the real dependency queries even
  // spawn, producing the flash where the button flips enabled →
  // disabled → enabled on first open. (For non-HTML kinds the body
  // query is disabled, so `htmlMetaLoading` is false and this is a
  // no-op.) Gating here also avoids publishing a subset of linked files
  // and leaving the rest Private.
  const metadataReady = !htmlMetaLoading && referencedQueriesSettled;
  const publishActionDisabled =
    isPublishing || !metadataReady || htmlEditsBlocking;
  const publishBlockedTooltip = htmlIsSaving
    ? "Saving edits…"
    : htmlPendingCount > 0
      ? htmlPendingCount === 1
        ? "1 edit pending save"
        : `${htmlPendingCount} edits pending save`
      : styling.tooltip;
  const publishActionLabel =
    visualState === "unpublished"
      ? "Publish"
      : hasUnpublishedDeps
        ? "Publish all"
        : "Publish updates";
  const showFooter = !isFullyUpToDate || documentEverPublished;

  const publishPanel = (
    <>
      {onboardingPublishActive && <style>{PUBLISH_CONFIRM_GLOW_STYLES}</style>}
      {/* Header: filename + open-in-new-tab on the right (when published).
           Status dot + label sit underneath as a subtitle so the document
           reads as the primary entity. */}
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <FileRecordTypeIcon
          contentType={record.content_type}
          originType={record.origin_type}
          className="h-[18px] w-[18px] shrink-0 mt-0.5"
        />
        <div className="min-w-0 flex-1">
          <p
            className="text-[14px] font-semibold text-foreground truncate"
            title={documentName}
          >
            {documentName}
          </p>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full shrink-0",
                statusDotClass,
                visualState === "stale" && "animate-pulse",
              )}
            />
            <p className="text-[12px] text-muted-foreground truncate">
              {visualState === "unpublished"
                ? "Not published"
                : visualState === "stale"
                  ? "Unpublished changes"
                  : styling.label}
            </p>
          </div>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {publishedUrl ? (
                <a
                  href={publishedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open in new tab"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05] transition-colors"
                >
                  <ExternalLink className="h-[15px] w-[15px]" />
                </a>
              ) : (
                <span
                  role="link"
                  aria-disabled="true"
                  aria-label="Open in new tab"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground/40 cursor-not-allowed"
                >
                  <ExternalLink className="h-[15px] w-[15px]" />
                </span>
              )}
            </TooltipTrigger>
            <TooltipContent>
              {publishedUrl
                ? "Open in new tab"
                : "Publish this file to open its public link"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Public link */}
      <div className={SECTION_DIVIDER} />
      <div className="px-3 py-2.5 space-y-1.5">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80">
          Public link
        </p>
        {publishedUrl ? (
          <div className="flex items-center gap-1.5">
            <input
              readOnly
              value={publishedUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="h-8 flex-1 min-w-0 rounded-lg border bg-foreground/[0.03] dark:bg-n-95/[0.04] px-2.5 text-[12px] font-mono text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleCopyUrl}
                    aria-label="Copy public URL"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05] transition-colors"
                  >
                    <Copy className="h-[14px] w-[14px]" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Copy</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <div className="flex h-8 items-center rounded-lg border border-dashed bg-muted/20 px-2.5 text-[12px] italic text-muted-foreground">
            Publish to generate link
          </div>
        )}
      </div>

      {/* Linked files (collapsible, collapsed by default) */}
      {linkedFiles.length > 0 && (
        <>
          <div className={SECTION_DIVIDER} />
          <Collapsible open={linkedFilesOpen} onOpenChange={setLinkedFilesOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="group/lf flex w-full items-center gap-2 h-9 rounded-lg px-3 text-left text-foreground/75 hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05] transition-colors cursor-pointer"
              >
                <ChevronRight
                  className={cn(
                    "h-[14px] w-[14px] shrink-0 text-muted-foreground/70 transition-transform",
                    linkedFilesOpen && "rotate-90",
                  )}
                />
                <span className="flex-1 min-w-0 text-[13px] font-medium">
                  Linked files
                </span>
                <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
                  {linkedFiles.length}
                </span>
                {hasUnpublishedDeps && (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-lc-warning/20 dark:text-amber-300">
                    {unpublishedDeps.length} private
                  </span>
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
              <div className="p-2">
                <ul className="max-h-56 overflow-y-auto rounded-lg border divide-y">
                  {linkedFiles.map(({ id, url, record: dep }) => {
                    // Record couldn't be loaded (e.g. it belongs to
                    // another account). Surface it anyway, linking out
                    // to the public URL scraped from the HTML.
                    if (!dep) {
                      return (
                        <li
                          key={id}
                          className="flex items-center gap-2 px-2.5 py-2"
                        >
                          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
                          <div className="min-w-0 flex-1">
                            <div
                              className="truncate text-[12px] text-foreground"
                              title={url}
                            >
                              Linked file
                            </div>
                          </div>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <ExternalLink className="h-2.5 w-2.5" />
                            View
                          </a>
                        </li>
                      );
                    }
                    const isPublished = fileRecordEverPublished(dep);
                    return (
                      <li
                        key={id}
                        className="flex items-center gap-2 px-2.5 py-2"
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
                        <div className="min-w-0 flex-1">
                          <div
                            className="truncate text-[12px] text-foreground"
                            title={dep.path}
                          >
                            {fileRecordDisplayName(dep)}
                          </div>
                        </div>
                        {isPublished ? (
                          <span className="flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300">
                            <Check className="h-2.5 w-2.5" />
                            Published
                          </span>
                        ) : (
                          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            Private
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}

      {/* Footer */}
      {showFooter && (
        <>
          <div className={SECTION_DIVIDER} />
          <div className="flex items-center gap-2 px-2 py-1.5">
            {documentEverPublished && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCascadeUnpublishDeps(false);
                  setUnpublishConfirmOpen(true);
                }}
                disabled={isMutating}
                className="h-8 rounded-lg text-[12px] text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                Unpublish
              </Button>
            )}
            <div className="flex-1" />
            {!isFullyUpToDate && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setOpen(false);
                    onEmbeddedClose?.();
                  }}
                  disabled={isMutating}
                  className="h-8 rounded-lg text-[12px] text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handlePublishAll}
                  isLoading={isPublishing}
                  disabled={publishActionDisabled || isUnpublishing}
                  className={cn(
                    "h-8 rounded-lg text-[12px]",
                    onboardingPublishActive && "_co_onb_publish_confirm_glow",
                  )}
                >
                  {publishActionLabel}
                </Button>
              </>
            )}
            {/* Embedded (action-dialog) mode has no popover to click away from,
                so once there's nothing left to publish the only close path is
                this explicit Done — otherwise the dialog stays open after a
                successful publish. */}
            {isFullyUpToDate && embeddedPanel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setOpen(false);
                  onEmbeddedClose?.();
                }}
                disabled={isMutating}
                className="h-8 rounded-lg text-[12px] text-muted-foreground hover:text-foreground"
              >
                Done
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );

  const unpublishDialog = (
    <AlertDialog
      open={unpublishConfirmOpen}
      onOpenChange={(next) => {
        if (!isUnpublishing) setUnpublishConfirmOpen(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unpublish document?</AlertDialogTitle>
          <AlertDialogDescription>
            The public link will stop resolving and the public copy will be
            removed. You can republish at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {publishedDeps.length > 0 && (
          <label className="flex items-start gap-2.5 rounded-md border bg-muted/20 p-3 cursor-pointer">
            <Checkbox
              checked={cascadeUnpublishDeps}
              onCheckedChange={(checked) =>
                setCascadeUnpublishDeps(checked === true)
              }
              disabled={isUnpublishing}
              className="mt-0.5"
            />
            <div className="space-y-0.5 text-sm">
              <div className="font-medium text-foreground">
                Also unpublish{" "}
                {publishedDeps.length === 1
                  ? "1 linked file"
                  : `${publishedDeps.length} linked files`}
              </div>
              <div className="text-xs text-muted-foreground">
                Only do this if no other published documents reference these
                files — their public URLs will also stop resolving.
              </div>
            </div>
          </label>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isUnpublishing}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // AlertDialogAction auto-closes on click; we want to keep
              // it open while the request is in flight so the spinner is
              // visible and accidental double-clicks are blocked.
              e.preventDefault();
              void handleUnpublish();
            }}
            disabled={isUnpublishing}
            className={buttonVariants({ variant: "destructive" })}
          >
            {isUnpublishing ? "Unpublishing…" : "Unpublish"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (embeddedPanel) {
    return (
      <>
        <div className="w-full">{publishPanel}</div>
        {unpublishDialog}
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                isLoading={isMutating}
                variant="ghost"
                size="sm"
                data-onboarding-target="artifact-publish-button"
                className={cn(
                  "h-7 px-2.5 text-xs font-medium gap-1.5",
                  styling.className,
                  className,
                )}
              >
                {styling.showPulse && (
                  <span className="h-1.5 w-1.5 rounded-full bg-lc-warning animate-pulse shrink-0" />
                )}
                {styling.label}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          {!open && (
            <TooltipContent>
              {htmlEditsBlocking ? publishBlockedTooltip : styling.tooltip}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        align="end"
        sideOffset={6}
        collisionPadding={20}
        className="w-[360px] p-2 rounded-2xl border dark:border-white/[0.08] shadow-xl shadow-black/10 bg-background/95 backdrop-blur-lg"
      >
        {publishPanel}
      </PopoverContent>

      {unpublishDialog}
    </Popover>
  );
}
